import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../../elements/ui/card';
import EmptyState from './EmptyState';
import { useTranslation } from 'react-i18next';
import { dailyRows, dayAxisProps, hasValues, type TimeRange } from './timeSeries';
import type { LineSeries } from '../../../models/time-series';

interface DailyBarChartProps {
  title: string;
  series: LineSeries[];
  range: TimeRange;
  unit?: string;
  /** Decimals on the daily mean. NH3 is single-digit ppm, so rounding it to whole numbers loses the reading. */
  precision?: number;
}

function BarTooltip({ active, payload, label, unit }: any) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map((entry: any) => {
        const min = entry.payload?.[`${entry.name}Min`];
        const max = entry.payload?.[`${entry.name}Max`];

        return (
          <div key={entry.name} className="mt-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.fill }} />
              <span className="text-gray-600">{entry.name}:</span>
              <span className="font-medium text-gray-900">
                {entry.value != null ? `${entry.value} ${unit || 'ppm'}` : '—'}
              </span>
            </div>
            {min != null && (
              <div className="text-gray-400 ml-3.5">
                Min: {min} {unit} / Max: {max} {unit}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const DailyBarChart: React.FC<DailyBarChartProps> = ({ title, series, range, unit = 'ppm', precision = 1 }) => {
  const { t } = useTranslation();

  const dailyData = React.useMemo(() => dailyRows(series, range, precision), [series, range, precision]);
  const hasData = hasValues(series);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <EmptyState
            title={`${t('f-no-data-placeholder', { value: title.toUpperCase() })}`}
            description={`${t('f-sensor-daily-averages-unavailable')}`}
          />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis {...dayAxisProps} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={40} />
              <Tooltip content={<BarTooltip unit={unit} />} />
              {series.map((s, idx) => (
                <Bar
                  key={s.name}
                  dataKey={s.name}
                  fill={s.strokeColor || `hsl(${(idx * 60) % 360}, 70%, 50%)`}
                  barSize={20}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default DailyBarChart;
